import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2,
  LogOut,
  Crown,
  Send,
  Palette,
  ChevronDown,
  Check,
  Gauge,
  FileText,
  Download,
  User,
  KeyRound,
  Mail, FileLock2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CheckoutDialog } from "./billing/CheckoutDialog";
import { WorkspacePanel } from "./WorkspacePanel";
import { BillingPanel } from "./BillingPanel";
import { DashboardKPIs } from "./DashboardKPIs";
import { HistoryPanel } from "./HistoryPanel";
import { AdministrationPanel } from "./AdministrationPanel";
import { PersonalizationPanel } from "./PersonalizationPanel";
import { WorkspaceDirectory } from "./WorkspaceDirectory";
import { UserDirectories } from "./UserDirectories";
import { PasswordPolicyChecklist } from "./PasswordPolicyChecklist";
import {
  Select as UiSelect, SelectTrigger as UiSelectTrigger, SelectValue as UiSelectValue,
  SelectContent as UiSelectContent, SelectItem as UiSelectItem,
} from "./ui/select";
import { api, apiError } from "../lib/api";
import { fmtAmountMinor } from "../lib/usePlans";
import { useAuth } from "../context/AuthContext";
import i18n, { countryName } from "../i18n";
import { useTranslation } from "react-i18next";

// Traduction (Mon espace) : les textes suivent la langue active ; la page parente s'abonne aux changements via useTranslation.
const tr = (k, o) => i18n.t(k, o);

const PLAN_ORDER = ["free", "pro", "team", "business", "enterprise"];

// Changement d'e-mail de connexion sécurisé : mot de passe requis + validation par code
// envoyé à la NOUVELLE adresse. L'ancienne reste active tant que le code n'est pas validé.
const EmailChangeDialog = ({ open, onOpenChange, currentEmail, onDone }) => {
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [maskedTarget, setMaskedTarget] = useState("");

  const reset = () => {
    setStep(1);
    setNewEmail("");
    setPassword("");
    setCode("");
    setBusy(false);
    setMaskedTarget("");
  };

  const handleOpenChange = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const requestChange = async () => {
    if (!newEmail.trim() || !password) {
      toast.error(tr("account:dashboard.renseignezLaNouvelleAdresse"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/account/email-change/request", {
        new_email: newEmail.trim(),
        current_password: password,
      });
      setMaskedTarget(data.new_email || newEmail.trim());
      setStep(2);
      toast.success(tr("account:dashboard.unCodeDeValidation"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verifyChange = async () => {
    if (!code.trim()) {
      toast.error(tr("account:dashboard.saisissezLeCodeRecu"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/account/email-change/verify", { code: code.trim() });
      onDone(data.user, data.directory_proposal_candidates || 0);
      toast.success(tr("account:dashboard.votreAdresseEMail"));
      handleOpenChange(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="email-change-dialog">
        <DialogHeader>
          <DialogTitle>{tr("account:dashboard.modifierLAdresseE")}</DialogTitle>
          <DialogDescription>
            {step === 1
              ? tr("account:dashboard.confirmezVotreMotDe")
              : tr("account:dashboard.saisissezLeCodeEnvoye", { v0: maskedTarget })}
          </DialogDescription>
        </DialogHeader>
        {step === 1 ? (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.adresseActuelle")}</Label>
              <Input value={currentEmail} readOnly disabled className="bg-slate-100 border-slate-200 text-slate-500" />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.nouvelleAdresseEMail")}</Label>
              <Input
                data-testid="email-change-new"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={tr("account:dashboard.nouvelleAdresseNc")}
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.motDePasseActuel")}</Label>
              <Input
                data-testid="email-change-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <Button
              data-testid="email-change-request-btn"
              onClick={requestChange}
              disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {tr("account:dashboard.envoyerLeCodeDe")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.codeDeValidation")}</Label>
              <Input
                data-testid="email-change-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                className="bg-slate-50 border-slate-200 tracking-[0.4em] text-center font-mono-token"
              />
            </div>
            <Button
              data-testid="email-change-verify-btn"
              onClick={verifyChange}
              disabled={busy}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {tr("account:dashboard.validerLaNouvelleAdresse")}
            </Button>
            <button
              type="button"
              data-testid="email-change-back-btn"
              onClick={() => setStep(1)}
              className="w-full text-xs text-slate-400 hover:text-slate-600"
            >
              {tr("account:dashboard.revenirALEtape")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AccountDashboard = () => {
  useTranslation(); // abonne tout « Mon espace » au changement de langue (re-rendu des panneaux enfants)
  const { user, loading, logout, setUser, activeWorkspace, activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const [branding, setBranding] = useState({ logo_url: "", sender_email: "", custom_domain: "" });
  const [savingBranding, setSavingBranding] = useState(false);
  const [brandingOpen, setBrandingOpen] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "billing" ? "billing" : "overview"));
  const [profile, setProfile] = useState({ first_name: "", last_name: "", phone: "", update_mode: "ask" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [proposalCandidates, setProposalCandidates] = useState(0);
  const [proposingAll, setProposingAll] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwdPolicy, setPwdPolicy] = useState(null);
  const [pwdExpiry, setPwdExpiry] = useState(null);
  const [pendingJoinCount, setPendingJoinCount] = useState(0);

  const loadData = useCallback(async () => {
    setLoadError(false);
    try {
      const [s, inv] = await Promise.all([
        api.get("/account/subscription"),
        api.get("/account/invoices"),
      ]);
      setSub(s.data);
      setInvoices(inv.data);
    } catch (e) {
      setLoadError(true);
    }
    try {
      const pol = await api.get("/account/password-policy");
      setPwdPolicy(pol.data.policy);
      setPwdExpiry(pol.data.expiry);
    } catch (e) {
      /* non bloquant */
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/connexion");
      return;
    }
    if (user) {
      setBranding(user.branding || { logo_url: "", sender_email: "", custom_domain: "" });
      setProfile({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        update_mode: user.directory_profile_update_mode || "ask",
      });
      loadData();
    }
  }, [user, loading, navigate, loadData, activeWorkspaceId]);

  // Badge "Mon Workspace" : nombre de demandes d'adhésion en attente (owner/admin uniquement).
  const loadPendingJoinCount = useCallback(async () => {
    const r = activeWorkspace?.role;
    if (!activeWorkspaceId || !(r === "owner" || r === "admin")) { setPendingJoinCount(0); return; }
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/join-requests`);
      setPendingJoinCount(Array.isArray(data) ? data.filter((r) => r.status === "pending").length : 0);
    } catch (e) {
      setPendingJoinCount(0);
    }
  }, [activeWorkspaceId, activeWorkspace]);

  useEffect(() => { loadPendingJoinCount(); }, [loadPendingJoinCount, tab]);

  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const changePlan = async (plan) => {
    // Offre payante : checkout obligatoire (facture Dolibarr -> paiement Mobupay -> activation après capture).
    if (plan !== "free") { setCheckoutPlan(plan); return; }
    try {
      const { data } = await api.post("/account/plan", { plan });
      setUser(data.user);
      await loadData();
      toast.success(tr("account:dashboard.forfaitV0Active", { v0: data.user.plan_config.name }));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/account/profile", {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        directory_profile_update_mode: profile.update_mode,
      });
      setUser(data.user);
      toast.success(tr("account:dashboard.informationsMisesAJour"));
      setProposalCandidates(data.directory_proposal_candidates || 0);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingProfile(false);
    }
  };

  const proposeAllDirectories = async () => {
    setProposingAll(true);
    try {
      await api.post("/account/directories/propose-updates");
      toast.success(tr("account:dashboard.vosModificationsOntEte"));
      setProposalCandidates(0);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setProposingAll(false);
    }
  };

  const changePassword = async () => {
    if (pwd.new_password.length < 8) {
      toast.error(tr("account:dashboard.leNouveauMotDe"));
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      toast.error(tr("account:dashboard.laConfirmationNeCorrespond"));
      return;
    }
    setChangingPwd(true);
    try {
      await api.post("/account/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      toast.success(tr("account:dashboard.motDePasseModifie"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setChangingPwd(false);
    }
  };

  const saveBranding = async () => {    setSavingBranding(true);
    try {
      const { data } = await api.post("/account/branding", branding);
      setUser(data.user);
      toast.success(tr("account:dashboard.personnalisationEnregistree"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingBranding(false);
    }
  };

  const downloadInvoice = async (inv) => {
    try {
      const res = await api.get(`/account/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Facture-${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(tr("account:dashboard.impossibleDeTelechargerLa"));
    }
  };

  if (loading || !user) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
        <p className="text-slate-500">{tr("account:dashboard.impossibleDeChargerVotre")}</p>
        <Button
          data-testid="dashboard-retry-btn"
          onClick={loadData}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
        >
          {tr("account:dashboard.reessayer")}
        </Button>
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const pc = sub.plan_config;
  // Prix d'affichage = bloc commercial backend (publication active, région du Workplace). JAMAIS PLAN_CONFIG.
  const com = sub.commercial || {};
  const comCurrency = com.currency || "EUR";
  const pubActive = !!com.publication_id; // publication commerciale active
  // Prix d'affichage : publication active -> bloc commercial EXCLUSIVEMENT (jamais PLAN_CONFIG).
  const comPrice = (key) => {
    const p = com.plans?.[key];
    if (p) {
      if (p.requires_quote || p.monthly_price_minor == null) return { label: tr("account:dashboard.surDevis"), ok: true };
      return { label: `${fmtAmountMinor(p.monthly_price_minor, p.currency || comCurrency)}/mois`, ok: true };
    }
    if (pubActive) return { label: "Tarif indisponible", ok: false };
    const lp = sub.all_plans?.[key];
    return { label: lp && lp.price_eur != null ? tr("account:dashboard.priceMonth", { v0: lp.price_eur }) : tr("account:dashboard.surDevis"), ok: true };
  };
  const currentPriceLabel = (() => {
    const p = com.current;
    if (p) {
      if (p.requires_quote || p.monthly_price_minor == null) return tr("account:dashboard.surDevis2");
      return tr("account:dashboard.v0Mois", { v0: fmtAmountMinor(p.monthly_price_minor, p.currency || comCurrency) });
    }
    if (pubActive) return "· Tarif indisponible";
    return pc.price_eur != null ? tr("account:dashboard.v0Mois2", { v0: pc.price_eur }) : tr("account:dashboard.surDevis2");
  })();
  const invoiceAmount = (inv) => {
    if (inv.amount_minor != null && inv.currency) return fmtAmountMinor(inv.amount_minor, inv.currency);
    return inv.amount_eur != null ? `${inv.amount_eur}€` : "—";
  };
  const quotaRef = sub.usage.soft_limit;
  const usagePct = quotaRef ? Math.min(100, Math.round((sub.usage.used / quotaRef) * 100)) : 0;
  const ent = sub.entitlements || {};
  const canBrand = ent.can_use_branding || ent.can_use_custom_domain;
  const showTeam = ent.max_members_effective == null || ent.max_members_effective > 1;
  const workspaceId = sub.workspace?.id;
  const role = activeWorkspace?.role || "owner";
  const canBilling = role === "owner" || role === "admin" || role === "billing_admin";
  const canAdmin = role === "owner" || role === "admin";
  const canDirectory = role !== "billing_admin";
  const allowedTabs = [
    "overview", "profile", "workspace",
    ...(canDirectory ? ["directory"] : []),
    "history",
    ...(canBilling ? ["billing"] : []),
    ...(canAdmin ? ["administration"] : []),
  ];
  const effectiveTab = allowedTabs.includes(tab) ? tab : "overview";

  return (
    <div data-testid="account-dashboard" className="space-y-8">
      <CheckoutDialog workspaceId={activeWorkspaceId} plan={checkoutPlan} onClose={() => setCheckoutPlan(null)} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-emerald-600">
            {tr("account:dashboard.espaceClient")}
          </span>
          <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-1">
            {tr("account:dashboard.bonjour")} {user.name || user.email}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="new-secret-btn"
            onClick={() => navigate("/generateur")}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
          >
            <Send className="w-4 h-4 mr-2" /> {tr("account:dashboard.nouvelEnvoi")}
          </Button>
          <Button
            data-testid="new-secure-file-btn"
            onClick={() => navigate("/fichiers")}
            variant="outline"
            className="border-emerald-300 bg-white hover:bg-emerald-50 text-emerald-800 font-semibold"
          >
            <FileLock2 className="w-4 h-4 mr-2" /> {tr("files:nav.menu")}
          </Button>
          <Button
            data-testid="account-logout-btn"
            onClick={() => { logout(); navigate("/"); }}
            variant="outline"
            className="border-slate-200 bg-slate-100 hover:bg-slate-100 text-slate-900"
          >
            <LogOut className="w-4 h-4 mr-2" /> {tr("account:dashboard.deconnexion")}
          </Button>
        </div>
      </div>

      <Tabs value={effectiveTab} onValueChange={setTab} className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList data-testid="dashboard-tabs" className="w-max">
            <TabsTrigger value="overview" data-testid="tab-overview">{tr("account:dashboard.tableauDeBord")}</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile">{tr("account:dashboard.monProfil")}</TabsTrigger>
            <TabsTrigger value="workspace" data-testid="tab-workspace">
              {tr("account:dashboard.monWorkspace")}
              {pendingJoinCount > 0 && (
                <span
                  data-testid="workspace-pending-tab-badge"
                  className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-amber-950 text-[10px] font-bold"
                >
                  {pendingJoinCount}
                </span>
              )}
            </TabsTrigger>
            {canBilling && <TabsTrigger value="billing" data-testid="tab-billing">{tr("account:dashboard.facturation")}</TabsTrigger>}
            {canDirectory && <TabsTrigger value="directory" data-testid="tab-directory">{tr("account:dashboard.annuaire")}</TabsTrigger>}
            <TabsTrigger value="history" data-testid="tab-history">{tr("account:dashboard.historique")}</TabsTrigger>
            {canAdmin && <TabsTrigger value="administration" data-testid="tab-administration">{tr("account:dashboard.administration")}</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <DashboardKPIs role={role} onManageSubscription={() => setTab("billing")} />
        </TabsContent>

        {/* Mon Workspace (§19-§30) — équipe, invitations, responsables facturation, sièges */}
        <TabsContent value="workspace" className="mt-6">
          <WorkspacePanel workspaceId={workspaceId} role={role} onJoinRequestsChange={loadPendingJoinCount} />
        </TabsContent>

        {/* Historique — journal global du workspace (Lot 5) : visibilité par rôle, filtres, export CSV */}
        <TabsContent value="history" className="mt-6">
          <HistoryPanel workspaceId={workspaceId} role={role} />
        </TabsContent>

        {/* Annuaire sécurisé — contacts du workspace (ne consomment pas de siège) */}
        {canDirectory && (
        <TabsContent value="directory" className="mt-6">
          <WorkspaceDirectory role={role} />
        </TabsContent>
        )}

        {/* Administration (Lot 6) — politiques de sécurité workspace + personnalisation */}
        {canAdmin && (
        <TabsContent value="administration" className="space-y-8 mt-6">
          <PersonalizationPanel workspaceId={workspaceId} role={role} entitlements={ent} />
          <AdministrationPanel workspaceId={workspaceId} role={role} plan={sub?.plan} />
        </TabsContent>
        )}

        <TabsContent value="profile" className="space-y-8 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="profile-section">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <User className="w-5 h-5 text-emerald-600" />
                <h3 className="font-heading text-lg font-semibold">{tr("account:dashboard.monProfil")}</h3>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.prenom")}</Label>
                    <Input
                      data-testid="profile-first-name"
                      value={profile.first_name}
                      onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))}
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.nom")}</Label>
                    <Input
                      data-testid="profile-last-name"
                      value={profile.last_name}
                      onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))}
                      className="bg-slate-50 border-slate-200"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.telephone")}</Label>
                  <Input
                    data-testid="profile-phone"
                    value={profile.phone}
                    onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="+687 ..."
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.eMailDeConnexion")}</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="profile-email"
                      value={user.email || ""}
                      readOnly
                      disabled
                      className="bg-slate-100 border-slate-200 text-slate-500 flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="change-email-btn"
                      onClick={() => setEmailDialogOpen(true)}
                      className="shrink-0 border-slate-200"
                    >
                      {tr("account:dashboard.modifier")}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {tr("account:dashboard.leChangementDAdresse")}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.societe")}</Label>
                  <Input
                    data-testid="profile-company"
                    value={activeWorkspace?.company_name || activeWorkspace?.name || "—"}
                    readOnly
                    disabled
                    className="bg-slate-100 border-slate-200 text-slate-500"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Société du Workspace actif. Elle change lorsque vous changez de Workspace.
                  </p>
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">Pays du Workspace actif</Label>
                  <Input
                    data-testid="profile-country"
                    value={activeWorkspace?.country_code ? countryName(activeWorkspace.country_code, i18n.language) : "—"}
                    readOnly
                    disabled
                    className="bg-slate-100 border-slate-200 text-slate-500"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.propagationDeMonProfil")}</Label>
                  <UiSelect value={profile.update_mode} onValueChange={(v) => setProfile((p) => ({ ...p, update_mode: v }))}>
                    <UiSelectTrigger data-testid="profile-update-mode" className="bg-slate-50 border-slate-200"><UiSelectValue /></UiSelectTrigger>
                    <UiSelectContent>
                      <UiSelectItem value="none">{tr("account:dashboard.aucuneNeRienProposer")}</UiSelectItem>
                      <UiSelectItem value="ask">{tr("account:dashboard.meDemanderAvantDe")}</UiSelectItem>
                      <UiSelectItem value="automatic_proposal">{tr("account:dashboard.proposerAutomatiquement")}</UiSelectItem>
                    </UiSelectContent>
                  </UiSelect>
                  <p className="text-xs text-slate-400 mt-1">
                    {tr("account:dashboard.lorsqueVousModifiezVotre")}
                  </p>
                </div>
                <Button
                  data-testid="save-profile-btn"
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                >
                  {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                  {tr("account:dashboard.enregistrer")}
                </Button>
                {proposalCandidates > 0 && (
                  <div data-testid="ask-propose-banner" className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs text-amber-800 mb-2">
                      {tr("account:dashboard.vosNouvellesCoordonneesPeuvent")} {proposalCandidates} {tr("account:dashboard.organisationSQuiVous")}
                    </p>
                    <Button
                      data-testid="propose-all-directories-btn"
                      size="sm"
                      onClick={proposeAllDirectories}
                      disabled={proposingAll}
                      className="bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold"
                    >
                      {proposingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      {tr("account:dashboard.proposerMesModifications")}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <KeyRound className="w-5 h-5 text-cyan-600" />
                <h3 className="font-heading text-lg font-semibold">{tr("account:dashboard.motDePasse")}</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.motDePasseActuel")}</Label>
                  <Input
                    data-testid="current-password-input"
                    type="password"
                    value={pwd.current_password}
                    onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))}
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.nouveauMotDePasse")}</Label>
                  <Input
                    data-testid="new-password-input"
                    type="password"
                    value={pwd.new_password}
                    onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))}
                    placeholder={tr("account:dashboard.8CaracteresMinimum")}
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <PasswordPolicyChecklist policy={pwdPolicy} value={pwd.new_password} expiry={pwdExpiry} />
                <div>
                  <Label className="text-slate-600 mb-1.5 block">{tr("account:dashboard.confirmerLeNouveauMot")}</Label>
                  <Input
                    data-testid="confirm-password-input"
                    type="password"
                    value={pwd.confirm}
                    onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                    className="bg-slate-50 border-slate-200"
                  />
                </div>
                <Button
                  data-testid="change-password-btn"
                  onClick={changePassword}
                  disabled={changingPwd || !pwd.current_password || !pwd.new_password}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
                >
                  {changingPwd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  {tr("account:dashboard.changerLeMotDe")}
                </Button>
              </div>
            </div>
          </div>
          <UserDirectories />
        </TabsContent>

        {canBilling && (
        <TabsContent value="billing" className="space-y-8 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-5 h-5 text-amber-600" />
                <h3 className="font-heading text-lg font-semibold">{tr("account:dashboard.votreForfait")}</h3>
              </div>
              <div className="flex items-baseline gap-2">
                <span data-testid="current-plan" className="font-heading text-3xl font-extrabold">{pc.name}</span>
                <span className="text-slate-500">{currentPriceLabel}</span>
              </div>
              <p className="text-sm text-slate-500 mt-3">
                {ent.monthly_secret_soft_limit != null
                  ? tr("account:dashboard.secretsIncludedPerMonth", { count: ent.monthly_secret_soft_limit })
                  : tr("account:dashboard.volumesSurMesure")}
              </p>
            </div>

            {/* Plan switcher */}
            <div className="glass rounded-2xl p-6">
              <h3 className="font-heading text-lg font-semibold mb-4">
                {role === "owner" ? tr("account:dashboard.changerDeForfait") : tr("account:dashboard.forfaitDeLEspace")}
              </h3>
              {role !== "owner" ? (
                <div className="text-sm text-slate-500">
                  {tr("account:dashboard.seulLeProprietaireDe")}{" "}
                  <span className="text-emerald-600 font-medium">{pc.name}</span>.
                </div>
              ) : (
              <div className="space-y-2">
                {PLAN_ORDER.map((key) => {
                  const p = sub.all_plans[key];
                  const active = sub.plan === key;
                  const price = comPrice(key);
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between rounded-lg px-4 py-3 border ${
                        active ? "bg-emerald-500/10 border-emerald-500/30" : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                        <div className={`text-xs ${price.ok ? "text-slate-500" : "text-amber-600 font-medium"}`} data-testid={`plan-price-${key}`}>
                          {price.label}
                          {p.monthly_secret_soft_limit != null ? ` · ${p.monthly_secret_soft_limit} secrets` : tr("account:dashboard.volumesSurMesure2")}
                        </div>
                      </div>
                      {active ? (
                        <Badge variant="outline" className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">
                          <Check className="w-3 h-3 mr-1" /> {tr("account:dashboard.actuel")}
                        </Badge>
                      ) : (
                        <Button
                          data-testid={`switch-plan-${key}`}
                          onClick={() => changePlan(key)}
                          disabled={!price.ok}
                          size="sm"
                          variant="outline"
                          className="border-slate-200 bg-slate-100 hover:bg-slate-100 text-slate-900 disabled:opacity-50"
                        >
                          {price.ok ? "Choisir" : "Indisponible"}
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
              <p className="text-xs text-slate-500 mt-3">
                {tr("account:dashboard.paiementEnLigneEpaync")}
              </p>
            </div>
          </div>

          {/* Documents financiers, infos entreprise, moyens de paiement, résiliation (Lot 4) */}
          <BillingPanel workspaceId={workspaceId} role={role} plan={sub.plan} />
        </TabsContent>
        )}
      </Tabs>

      <EmailChangeDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        currentEmail={user.email || ""}
        onDone={(u, candidates) => {
          setUser(u);
          if (candidates > 0) { setProposalCandidates(candidates); setTab("profile"); }
        }}
      />
    </div>
  );
};
